/* eslint-disable */
// lint to be fixed in this ticket: https://lastbrand.atlassian.net/browse/ECOMP-3295
import { IS_PROD_BUILD } from '@src/config/env'
import { isEmpty } from '@src/utils/functions/utils'

import { FEATURES, MODULES } from '../../constants/template-info'
import type { CustomAttribute } from '../../domains/shopify/CustomAttribute'
import { getCartIDAndAuth } from '../../hooks/cart/hooks/new-cart-helper'
import queryCheckout from '../../hooks/shopify/queryCheckout'
import { attrWithoutUnderscore } from '../../organisms/appLayout/molecules/cartPopUp/cart.helper'
import isBrowser from '../../utils/isBrowser'
import { captureException, captureFatalException } from '../../utils/sentryUtil'
import setCookie from '../../utils/setCookie'
import withRetry from '../../utils/with-retry'
import {
  addItemToCart,
  applyDiscountCode,
  buyNowCartApi,
  getCarts,
  getCartWithId,
  getIsCartActive,
  getOrCreateCheckout,
  getPreviousCarts,
  mergeCustomerCart,
  updateGiftingDetails,
  updateGiftingFlag,
  updateItemQuantity,
} from '../cartService/cart.service'
import { setCartAuthCodeToLocalStorage } from '../cartService/cart.service.helper'
import type { GiftingDetails, LineItems } from '../cartService/cart.service.types'
import productService from '../productService'

import {
  deletePrevCheckoutId,
  getPrevCheckoutId,
  getNewCartData,
  getNewCheckoutFromLocalStorage,
  updateNewCheckoutInLocalStorage,
} from './newCheckoutService.helper'
import {
  CHECKOUT_TEST_KEY_VALUE,
  CHECKOUT_TEST_PARAM_VALUE,
  LOCAL_STORAGE_NEW_CART_ID_KEY,
} from './offshopify.constants'
import { LOCAL_STORAGE_CHECKOUT_ID_KEY, LOCAL_STORAGE_CHECKOUT_OBJ_KEY } from './shopifyCheckoutService.constants'
import { getDiscountCodeIfPresentFromLocalStorage, getUpdatedUrl } from './shopifyCheckoutService.helper'
import { ShopifyClientBuilder } from './ShopifyClientBuilder'

const getCheckoutId = () => {
  return window.localStorage.getItem(LOCAL_STORAGE_NEW_CART_ID_KEY)
}

export type TBuyNow = { shopifyVariantId: number; customAttributes: any[] }[]

export const getCartData = async () => {
  let result = await getNewCartData()
  // Syncing data if old keys are available
  const checkoutId = window.localStorage.getItem(LOCAL_STORAGE_CHECKOUT_ID_KEY)
  if (isBrowser() && checkoutId) {
    const shopifyClient = ShopifyClientBuilder.getShopifyClient()
    const checkoutResponse = await queryCheckout(shopifyClient, {
      checkoutId,
    })
    const shopifyProductIds: string[] = []
    const variantItemData: any = {}
    const itemList: any[] = []
    checkoutResponse?.data?.node?.lineItems?.edges?.forEach(item => {
      const productId = item?.node?.variant?.product?.id?.split('/').pop()
      const variantId = item?.node?.variant?.id?.split('/')?.pop()
      const quantity = item?.node?.quantity
      const customAttributes = item?.node?.customAttributes.map(({ key, value }) => ({
        key,
        value,
      }))

      if (productId && variantId) {
        shopifyProductIds.push(productId)
        variantItemData[variantId] = {
          variantId,
          quantity,
          customAttributes,
        }
        itemList.push({
          variantId,
          productId,
        })
      }
    })

    const res = await productService.getProducts({
      shopifyProductIds,
      useLite: true,
      isChildProduct: true,
    })
    const productMap: any = res.reduce((acc, curr) => {
      return {
        ...acc,
        [curr.shopifyProductId]: curr,
      }
    }, {})

    const lineItems: LineItems = []
    itemList.forEach(item => {
      const variantInfo = variantItemData[item.variantId]
      const product = productMap[item.productId]
      const productData = product?.variants?.find((variant: any) => variant.shopifyVariantId == variantInfo.variantId)
      if (productData) {
        lineItems.push({
          variantId: productData.id,
          quantity: variantInfo.quantity,
          attributes: variantInfo.customAttributes,
        })
      }
    })

    if (lineItems.length > 0) {
      const cartId = result?.id ?? getCheckoutId()
      if (cartId) {
        await addItemToCart(cartId, lineItems)
        result = await getCartWithId(cartId)
      } else {
        const previousCartId = getPrevCheckoutId()
        if (previousCartId) {
          result = await getCarts(lineItems, previousCartId)
          deletePrevCheckoutId()
        } else {
          result = await getCarts(lineItems)
        }
      }
    }
    // await replaceCheckoutLineItems(shopifyClient, {
    //   lineItems: [],
    //   checkoutId: checkoutId,
    // });
    window.localStorage.removeItem(LOCAL_STORAGE_CHECKOUT_ID_KEY)
    window.localStorage.removeItem(LOCAL_STORAGE_CHECKOUT_OBJ_KEY)
  }
  updateNewCheckoutInLocalStorage(result)
  return result
}

export class NewCheckoutService {
  isInitialized = false
  initializingPromise: Promise<any> | null = null

  constructor() {
    withRetry(this.initializeCheckout, 3).catch((quinceError: unknown) => {
      captureException(quinceError, {
        module: MODULES.CHECKOUT,
        feature: FEATURES.INIT_CHECKOUT,
        extraInfo: {
          capturedFrom: 'initializeCheckout in ShopifyCheckoutService',
        },
      })
    })
  }

  getCheckoutId = () => {
    return getCheckoutId()
  }

  mergeCart = async (cartId: string) => {
    const mergedCart = await mergeCustomerCart(cartId)
    await getIsCartActive()
    updateNewCheckoutInLocalStorage(mergedCart)
    return mergedCart
  }

  takeToCheckout = async (checkoutBucket?: string) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.error('Cart Id not available')
      return
    }
    try {
      await this.handleShopifyDiscount()
      const res = await getOrCreateCheckout(cartId)
      const url = new URL(window.location.origin)
      url.pathname = 'checkout'
      url.searchParams.set('checkoutId', res.checkoutId)
      url.searchParams.set('checkoutAuthCode', res.checkoutAuthCode)
      // TODO: remove this once the experiment is over
      if (checkoutBucket) {
        if (checkoutBucket.includes('_')) {
          // Handle combined bucket case
          const [bannerBucket, preloadBucket] = checkoutBucket.split('_')
          url.searchParams.set('bannerCroBucket', bannerBucket!)
          url.searchParams.set('preloadComponent', preloadBucket!)
        } else if (checkoutBucket.startsWith('Show')) {
          // Handle only banner experiment case
          url.searchParams.set('bannerCroBucket', checkoutBucket)
        } else {
          // Handle only preload experiment case
          url.searchParams.set('preloadComponent', checkoutBucket)
        }
      }

      if (!IS_PROD_BUILD) {
        const testId = url.searchParams.get(CHECKOUT_TEST_PARAM_VALUE)
        if (testId) {
          setCookie(CHECKOUT_TEST_KEY_VALUE, testId, 60)
          url.searchParams.set(CHECKOUT_TEST_PARAM_VALUE, testId)
        }
      }
      const finalCheckoutUrl = getUpdatedUrl(url.href)
      window.location.href = finalCheckoutUrl
    } catch (error) {
      console.error('Error occured on takeToCheckout')
    }
  }

  handleShopifyDiscount = async () => {
    const discountCode = getDiscountCodeIfPresentFromLocalStorage()
    if (discountCode) {
      await this.addDiscountToCheckout(discountCode)
    }
  }

  buyNowCart = async (variants: LineItems) => {
    const { cartID, authCode } = getCartIDAndAuth(true)
    const parentCartAuthMapping =
      cartID && authCode
        ? {
            id: cartID,
            authCode: authCode,
          }
        : undefined
    const response = await buyNowCartApi(variants, parentCartAuthMapping)
    if (response) {
      setCartAuthCodeToLocalStorage(response?.parentCartAuthMapping.authCode)
      const newCart = await getCartWithId(response.parentCartAuthMapping.id)
      updateNewCheckoutInLocalStorage(newCart)
      return { response, lineItems: newCart.lineItems }
    }
    captureFatalException(response, {
      capturedFrom: 'buyNowCart in NewCheckoutService',
      variants,
      parentCartAuthMapping,
    })
  }

  addDiscountToCheckout = async (discountCode: string) => {
    try {
      const cartId = this.getCheckoutId()
      if (!cartId) {
        console.warn('Cart ID is not available when adding discount to cart in addDiscountToCheckout')
        return
      }
      return await applyDiscountCode(cartId, discountCode)
    } catch (error) {
      console.error('Error occured while applying discount code :: ', error)
      captureFatalException(error, {
        capturedFrom: 'addDiscountToCheckout in NewCheckoutService',
      })
      return
    }
  }

  initializeCheckout = async () => {
    if (this.initializingPromise) {
      return await this.initializingPromise
    }
    this.initializingPromise = getCartData()
      .catch((error: unknown) => {
        console.error('Error occurred in initializeCheckout ', error)
        throw error // Rethrow the error so that the promise chain handles it correctly.
      })
      .finally(() => {
        this.initializingPromise = null
      })
    return await this.initializingPromise
  }

  async getCheckout(ignoreLocalStorage?: boolean) {
    let checkout = ignoreLocalStorage ? null : getNewCheckoutFromLocalStorage()
    if (!checkout) {
      checkout = await this.initializeCheckout()
    }
    return { ...checkout, lineItems: checkout?.lineItems }
  }

  removeLineItem = async (lineItemId: string, quantity: number) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.warn('Cart ID is not available')
      return
    }
    return await this.updateLineItem(lineItemId, quantity, -1 * quantity)
  }

  removeLineItems = async (lineItemIds: any) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.warn('Cart ID is not available')
      return
    }
    return await this.updateLineItems(lineItemIds)
  }

  updateLineItem = async (lineItemId: string | null, _: number, adjustment: number) => {
    return this.updateLineItems([
      {
        lineItemId: lineItemId,
        adjustment: adjustment,
      },
    ])
  }

  updateLineItems = async (
    lineItems: {
      lineItemId: string | null | undefined
      adjustment: number
    }[],
  ) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.warn('Cart ID is not available')
      return
    }

    const resp = await updateItemQuantity(
      cartId,
      lineItems.map(lineItem => ({
        ...lineItem,
        lineItemId: lineItem.lineItemId?.split('_')?.[0],
      })),
    )
    updateNewCheckoutInLocalStorage(resp)
    return resp
  }

  addToCart = async (shopifyVariantId: number, quantity = 1, customAttributes: any[] = []) => {
    return await this.addMultipleToCart([
      {
        shopifyVariantId,
        quantity,
        customAttributes,
      },
    ])
  }

  addMultipleToCart = async (
    variantDetails: {
      shopifyVariantId: number
      quantity: number
      customAttributes: any[]
    }[],
  ) => {
    const cartId = this.getCheckoutId()
    const checkout = await this.getCheckout()
    const checkoutMap =
      checkout?.lineItems?.reduce((acc: object, lineItem: any) => {
        return {
          ...acc,
          [lineItem.variantId]: lineItem,
        }
      }, {}) || {}

    const lineItems: LineItems = variantDetails.map(
      ({ shopifyVariantId: variantId, quantity, customAttributes: attributes }) => {
        const oldLineItemCreatedAt = checkoutMap[variantId]?.attributes?.find(
          (ca: { key: string; value: string }) => attrWithoutUnderscore(ca.key) === 'createdAt',
        )?.value

        return {
          variantId,
          quantity,
          attributes: [
            ...attributes,
            {
              key: '_createdAt',
              value: oldLineItemCreatedAt || `${Date.now()}`,
            },
          ],
        }
      },
    )

    let cart
    if (cartId) {
      cart = await addItemToCart(cartId, lineItems)
    } else {
      const previousCartId = getPrevCheckoutId()
      if (previousCartId) {
        cart = await getCarts(lineItems, previousCartId)
        deletePrevCheckoutId()
      } else {
        cart = await getCarts(lineItems)
      }
    }
    updateNewCheckoutInLocalStorage(cart)
    return cart
  }

  getCartTotalItemsCount = async () => {
    const checkout = await this.getCheckout()
    return checkout?.lineItems?.reduce((acc: number, item: any) => (acc += item.quantity as number), 0)
  }

  getPreviousCartData = async (prevCartId: string) => {
    const cart = await getPreviousCarts(prevCartId)
    if (cart) {
      updateNewCheckoutInLocalStorage(cart)
    }
    deletePrevCheckoutId()
    return cart
  }

  updateGiftingDetailsForCart = async (giftingDetails: GiftingDetails) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.warn('Cart ID is not available')
      return
    }

    try {
      const cartData = await updateGiftingDetails(cartId, giftingDetails)
      updateNewCheckoutInLocalStorage(cartData)
      return cartData
    } catch (error) {
      console.error('Error occurred while updating gifting details :: ', error)
      captureFatalException(error, {
        capturedFrom: 'updateGiftingDetails in NewCheckoutService',
      })
      throw error
    }
  }

  updateGiftingFlag = async (isGift: boolean) => {
    const cartId = this.getCheckoutId()
    if (!cartId) {
      console.warn('Cart ID is not available')
      return
    }
    try {
      const cartData = await updateGiftingFlag(cartId, isGift)
      updateNewCheckoutInLocalStorage(cartData)
      return cartData
    } catch (error) {
      console.error('Error occurred while updating gifting flag :: ', error)
      captureFatalException(error, {
        capturedFrom: 'updateGiftingFlag in NewCheckoutService',
      })
      throw error
    }
  }

  static calculateDiscountForAllProdcut(lineItem: any) {
    const {
      totalDiscount: discount,
      subtotalAmount: actualPrice,
      totalAmount,
      amountPerQuantity,
    } = lineItem.costSummary || {}

    return {
      price: totalAmount,
      actualPrice,
      discount,
      amountPerQuantity,
    }
  }

  static renderCustomAttributeCustomization(customAttributes: CustomAttribute[]): string | undefined {
    const attribute = customAttributes.filter(
      attribute => attribute.key === 'Customization' || attribute.key === 'Engraving',
    )
    if (!isEmpty(attribute as any[])) {
      return `  |  ${attribute[0]?.key}: ${attribute[0]?.value}`
    }
    return
  }
}

export default NewCheckoutService

/* eslint-enable */
